// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import { useState, useRef, useEffect } from 'react';
import * as ScanUtils from '../../utils/game/ScanningUtils';
import GameConfig from '../../config/GameConfig';
import EventBus, { EVENT_TYPES } from '../../utils/system/EventBus';
import STORAGE_KEYS from '../../utils/system/STORAGE_KEYS';
import MissionManager from '../../utils/game/MissionManager';

/**
 * Custom hook for managing scanning logic and state
 * Extracted from NavigationPanel to reduce bundle size
 */
export const useScanningLogic = ({
  selectedSystem,
  currentLocation,
  antimatterFuel,
  getSystemHealth,
  getDisplayDistance,
  getCurrentUniverseData,
  addFuel,
  addCredits,
  removeFuel,
  setActiveEvent
}) => {
  const [scanProgress, setScanProgress] = useState(0);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  
  // Initialize scanned systems from localStorage
  const [scannedSystems, setScannedSystems] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.SCANNED_SYSTEMS);
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch (error) {
        console.error('[ScanningLogic] Failed to parse scanned systems:', error);
      }
    }
    return new Set();
  });
  
  // Initialize stored scan results from localStorage
  const [storedScanResults, setStoredScanResults] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.STORED_SCAN_RESULTS);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('[ScanningLogic] Failed to parse scan results:', error);
      }
    }
    return {};
  });

  const intervalRef = useRef(null);

  // Persist scanned systems to localStorage
  const persistScannedSystems = (systems) => {
    localStorage.setItem(STORAGE_KEYS.SCANNED_SYSTEMS, JSON.stringify(Array.from(systems)));
  };

  // Persist scan results to localStorage
  const persistScanResults = (results) => {
    localStorage.setItem(STORAGE_KEYS.STORED_SCAN_RESULTS, JSON.stringify(results));
  };

  // Generate scan results for a system
  const generateScanResults = (system) => {
    // Check if this system has been scanned before
    const isRescan = scannedSystems.has(system.id);
    
    // Get sensor health and upgrade bonus
    const sensorHealth = getSystemHealth('SENSORS');
    const sensorUpgradeBonus = 0; // Placeholder for future upgrade integration
    const distanceFromHere = getDisplayDistance(system);
    
    // Use current universe data to get aliens config
    const universeData = getCurrentUniverseData();
    const universeAliens = universeData?.aliens || null;
    
    // Use ScanningUtils to generate results (pure function - no side effects)
    return ScanUtils.generateScanResults(
      system,
      currentLocation,
      distanceFromHere,
      sensorHealth,
      sensorUpgradeBonus,
      null, // addFuel not needed - rewards applied in useEffect
      null, // addCredits not needed - rewards applied in useEffect
      isRescan,
      storedScanResults[system.id],
      universeAliens
    );
  };

  // Apply scan rewards when scan completes (useEffect to avoid render-time updates)
  useEffect(() => {
    if (scanResults && scanProgress >= 100 && !scanResults.isRescan) {
      // Apply credits bonus
      if (scanResults.creditsBonus > 0) {
        addCredits(scanResults.creditsBonus, 'scan');
      }
      
      // Apply discovery rewards (fuel and credits from discoveries)
      if (scanResults.discoveryRewards && scanResults.discoveryRewards.length > 0) {
        scanResults.discoveryRewards.forEach(reward => {
          if (reward.type === 'fuel') {
            addFuel(reward.amount);
          } else if (reward.type === 'credits') {
            addCredits(reward.amount, 'scan discovery');
          }
        });
      }
    }
  }, [scanResults, scanProgress, addCredits, addFuel]);

  // Start scanning process
  const startScan = () => {
    if (!selectedSystem) {
      return;
    }
    
    // Check fuel cost
    const fuelCost = GameConfig.scanning.fuelCost;
    const currentFuel = antimatterFuel;
    
    if (currentFuel < fuelCost) {
      setActiveEvent({
        type: 'WARNING',
        title: '⚠️ INSUFFICIENT FUEL',
        message: `Deep scan requires ${fuelCost}% fuel.\n\nCurrent fuel: ${Math.round(currentFuel)}%\n\nRefuel at a station or find fuel deposits to continue scanning.`,
        color: '#ff4444',
        icon: '⚠️',
        actions: [
          {
            label: 'CANCEL SCAN',
            effect: () => setActiveEvent(null)
          }
        ]
      });
      return;
    }
    
    // Consume fuel for scan
    removeFuel(fuelCost);
    
    setShowScanModal(true);
    setScanProgress(0);
    setScanResults(null);
    
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      setScanProgress(currentProgress => {
        const newProgress = currentProgress + 2;
        
        if (newProgress >= 100) {
          clearInterval(intervalRef.current);
          
          // Generate scan results
          const results = generateScanResults(selectedSystem);
          setScanResults(results);
          
          // Defer event emission to avoid updating during render
          setTimeout(() => {
            // Save last scanned system to localStorage for SystemTab
            localStorage.setItem(STORAGE_KEYS.LAST_SCANNED_SYSTEM, JSON.stringify(selectedSystem));
            
            EventBus.emit(EVENT_TYPES.SCAN_COMPLETE, {
              system: selectedSystem,
              results: results
            });
            
            // Track scan for missions
            const completedMissions = MissionManager.recordScan(selectedSystem.name);
            completedMissions.forEach(result => {
              EventBus.emit(EVENT_TYPES.MISSION_COMPLETE, {
                mission: result.mission,
                credits: result.rewards.credits,
                reputation: result.rewards.reputation
              });
            });
            
            // Also emit SYSTEM_SELECTED so SystemTab has the data when it mounts
            EventBus.emit(EVENT_TYPES.SYSTEM_SELECTED, {
              system: selectedSystem,
              getDisplayDistance: () => 0 // Scan doesn't have distance context
            });
          }, 0);
          
          // Store results for future rescans (only if first-time scan)
          if (!results.isRescan) {
            setStoredScanResults(prev => {
              const updated = {
                ...prev,
                [selectedSystem.id]: results
              };
              persistScanResults(updated);
              return updated;
            });
          }
          
          // Mark system as scanned (allow re-scans with no new rewards)
          setScannedSystems(prev => {
            const updated = new Set([...prev, selectedSystem.id]);
            persistScannedSystems(updated);
            return updated;
          });
          
          return 100;
        }
        
        return newProgress;
      });
    }, GameConfig.scanning.scanProgressInterval);
  };

  // Close scan modal
  const closeScanModal = () => {
    setShowScanModal(false);
    setScanProgress(0);
    setScanResults(null);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  return {
    scanProgress,
    showScanModal,
    scanResults,
    scannedSystems,
    storedScanResults,
    startScan,
    closeScanModal,
    setScanResults,
    setScannedSystems,
    setStoredScanResults
  };
};

export default useScanningLogic;